import { createContext, useContext, useState, useEffect } from 'react';
import { verifyAdminToken, adminLogout, removeStoredToken } from '@/lib/api';

const AuthContext = createContext(null);

// Store must_change_password flag separately
const MUST_CHANGE_KEY = 'rts_must_change_password';

export const getMustChangePassword = () => {
  return localStorage.getItem(MUST_CHANGE_KEY) === 'true';
};

export const setMustChangePassword = (value) => {
  if (value) {
    localStorage.setItem(MUST_CHANGE_KEY, 'true');
  } else {
    localStorage.removeItem(MUST_CHANGE_KEY);
  }
};

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePasswordState] = useState(getMustChangePassword());

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const adminData = await verifyAdminToken();
      if (adminData) {
        setAdmin(adminData);
        const mustChange = adminData?.must_change_password || false;
        setMustChangePasswordState(mustChange);
        setMustChangePassword(mustChange);
      } else {
        setAdmin(null);
        setMustChangePasswordState(false);
        setMustChangePassword(false);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setAdmin(null);
      setMustChangePasswordState(false);
      setMustChangePassword(false);
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      await adminLogout();
    } catch (error) {
      console.error('Logout error:', error);
      removeStoredToken();
    }
    setAdmin(null);
    setMustChangePasswordState(false);
    setMustChangePassword(false);
  };

  const login = (adminData, mustChange = false) => {
    setAdmin(adminData);
    setMustChangePasswordState(mustChange);
    setMustChangePassword(mustChange);
  };

  return (
    <AuthContext.Provider value={{ 
      admin, 
      loading, 
      login, 
      logout, 
      checkAuth, 
      isAuthenticated: !!admin,
      mustChangePassword: mustChangePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
